import { Box, Drawer, Group, Stack, Text } from "@mantine/core";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { visibleNavEntries } from "~/access/membership";
import { useAccess } from "~/access/useAccess";
import { authProfileAtom, userAtom } from "~/atoms";
import Center from "~/components/Center";
import { useDemo, useOrgs } from "~/hooks";
import { useRenderedPathname } from "~/hooks/useRenderedPathname";
import { navEntryFor } from "~/utils/navManifest";

import {
  DEFAULT_BORDER_RADIUS,
  DEFAULT_SPACING,
  DEFAULT_SPACING_MOBILE,
} from "../../utils/Constants";
import { optimisePageTitle } from "../../utils/Helper";
import RoundIconButton from "../Button/RoundIconButton";
import LanguageSwitcher from "../LanguageSwitcher";
import { Footer } from "../PageContainer";
import SvgIcon from "../SvgIcon";
import ThemeModeSwitch from "../ThemeModeSwitch";
import LeftSidebar from "./LeftSidebar";
import LeftSidebarMobile from "./LeftSidebarMobile";
import Logo from "./Logo";
import ShowGMPContact from "./ShowGMPContact";
import SwitchDemo from "./SwitchDemo";
import SwitchOrg from "./SwitchOrg";
import SwitchRole from "./SwitchRole";
import SwitchView from "./SwitchView";

export const SIDEBAR_WIDTH = 80;

interface Props {
  title?: string;
  children?: React.ReactNode;
}

const Nav = ({ title, children }: Props) => {
  const { t } = useTranslation("common");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const user = useAtomValue(userAtom);
  const authProfile = useAtomValue(authProfileAtom);
  const { currentOrg, orgs } = useOrgs();
  const { isDemoOrg } = useDemo();
  const { canSee } = useAccess();

  const pathname = useRenderedPathname(); // path of the rendered page

  const navEntries = visibleNavEntries(canSee);
  const currentNavEntry = navEntryFor(pathname);

  if (!orgs || !user) return null;

  return (
    <Box style={{ display: "flex", flex: 1, height: "100vh" }}>
      <Group flex={1} gap={0} wrap="nowrap" align="stretch">
        <Drawer
          opened={mobileSidebarOpen}
          onClose={() => setMobileSidebarOpen(false)}
          withCloseButton={false}
          padding={0}
          size={SIDEBAR_WIDTH * 3}
        >
          <LeftSidebarMobile
            navEntries={navEntries}
            currentOrg={currentOrg}
            closeSidebar={() => setMobileSidebarOpen(false)}
          />
        </Drawer>
        <Stack display={{ base: "none", md: "flex" }} gap={0}>
          <LeftSidebar navEntries={navEntries} />
        </Stack>
        <Stack w="100%" h="100%" gap={0}>
          <Group
            gap={8}
            wrap="nowrap"
            justify="space-between"
            h={36}
            px={{ base: DEFAULT_SPACING_MOBILE * 8, md: DEFAULT_SPACING * 8 }}
            style={{
              backgroundColor: "var(--mantine-color-body)",
              borderBottom: "1px solid var(--mantine-color-border-primary)",
              flexShrink: 0,
            }}
          >
            <Group gap={4} wrap="nowrap">
              <Center direction="row" display={{ base: "flex", md: "none" }}>
                <RoundIconButton
                  onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
                >
                  <SvgIcon name="Menu" />
                </RoundIconButton>
                <Logo size={20} />
              </Center>
              {isDemoOrg ? <SwitchDemo /> : <SwitchOrg orgs={orgs} />}
            </Group>
            <Group gap={8} wrap="nowrap" justify="flex-end">
              <SwitchView />
              <Group
                gap={8}
                wrap="nowrap"
                display={{ base: "none", md: "flex" }}
              >
                <Text size="xs">{authProfile?.displayName ?? ""}</Text>
                <SwitchRole />
              </Group>
              {currentOrg?.idString === "GMP" && <ShowGMPContact />}
              <LanguageSwitcher />
              <ThemeModeSwitch size="small" />
            </Group>
          </Group>
          <Stack
            w="100%"
            h="100%"
            p={{ base: DEFAULT_SPACING_MOBILE * 8, md: DEFAULT_SPACING * 8 }}
            pb={{
              base: (DEFAULT_SPACING_MOBILE / 3) * 8,
              md: (DEFAULT_SPACING / 3) * 8,
            }}
            gap={8}
            bg="var(--mantine-color-bg-secondary)"
            style={{ overflow: "auto" }}
          >
            {title && (
              <Group gap={16} wrap="nowrap">
                {currentNavEntry && (
                  <Center
                    w={48}
                    h={48}
                    bg="var(--mantine-primary-color-filled)"
                    style={{ borderRadius: DEFAULT_BORDER_RADIUS }}
                  >
                    <SvgIcon name={currentNavEntry.icon} color="white" />
                  </Center>
                )}
                <Text size="xl" fw={600} data-testid="page-title">
                  {currentNavEntry
                    ? optimisePageTitle(
                        t(`sectionNames.${currentNavEntry.sectionName}`),
                      )
                    : title}
                </Text>
              </Group>
            )}
            <Stack
              w={{
                base: `calc(100vw - ${DEFAULT_SPACING_MOBILE * 8 * 2}px)`,
                md: `calc(100vw - ${SIDEBAR_WIDTH + DEFAULT_SPACING * 8 * 2}px)`,
              }}
              h="100%"
              gap={0}
              justify="space-between"
              style={{ overflow: "auto" }}
            >
              {children}
              <Footer />
            </Stack>
          </Stack>
        </Stack>
      </Group>
    </Box>
  );
};

export default Nav;
